import { useEffect, useRef } from 'react'

type Alarmable = { id: string | number; title: string; start_at: string }

const STORAGE_KEY = 'atlas:event-alarms'
const FIRED_KEY   = 'atlas:event-alarms:fired'
const DEFAULT_LEAD_MIN = 10

export function getEventLeadMin(eventId: string | number): number {
  try {
    const map = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}')
    const v = map[String(eventId)]
    return typeof v === 'number' ? v : DEFAULT_LEAD_MIN
  } catch {
    return DEFAULT_LEAD_MIN
  }
}

export function setEventLeadMin(eventId: string | number, leadMin: number | null): void {
  try {
    const map = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}')
    if (leadMin === null) delete map[String(eventId)]
    else map[String(eventId)] = leadMin
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch { /* ignore */ }
}

function fired(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(FIRED_KEY) ?? '[]')) }
  catch { return new Set() }
}
function markFired(id: string): void {
  try {
    const s = fired(); s.add(id)
    // Keep ≤ 500 entries to bound localStorage size.
    const arr = Array.from(s).slice(-500)
    localStorage.setItem(FIRED_KEY, JSON.stringify(arr))
  } catch { /* ignore */ }
}

/**
 * Schedules browser notifications for a list of upcoming events.
 * Lead-time per-event is read from localStorage (default 10 min).
 * Each (event id × scheduled fire time) only fires once across reloads.
 */
export function useEventAlarms(items: Alarmable[]): void {
  const timersRef = useRef<number[]>([])
  useEffect(() => {
    // Cancel previous batch
    timersRef.current.forEach(t => window.clearTimeout(t))
    timersRef.current = []

    if (typeof window === 'undefined' || !('Notification' in window)) return
    if (Notification.permission === 'default') Notification.requestPermission().catch(() => {})

    const now = Date.now()
    const seen = fired()
    for (const it of items) {
      const startTs = new Date(it.start_at.replace(' ', 'T')).getTime()
      if (Number.isNaN(startTs)) continue
      const lead = getEventLeadMin(it.id) * 60_000
      const fireAt = startTs - lead
      const delay = fireAt - now
      const fireKey = `${it.id}:${fireAt}`
      if (seen.has(fireKey)) continue
      if (delay < -5_000) continue // already past
      const trigger = () => {
        try {
          if (Notification.permission === 'granted') {
            const n = new Notification(`⏰ ${it.title}`, {
              body: minutesUntil(startTs) + ' until start',
              tag: String(it.id),
              requireInteraction: false,
            })
            setTimeout(() => n.close(), 15_000)
          }
        } catch { /* ignore */ }
        markFired(fireKey)
      }
      if (delay <= 0) trigger()
      else if (delay < 2_147_000_000) {
        const handle = window.setTimeout(trigger, delay)
        timersRef.current.push(handle)
      }
    }
    return () => {
      timersRef.current.forEach(t => window.clearTimeout(t))
      timersRef.current = []
    }
  }, [items])
}

function minutesUntil(ts: number): string {
  const m = Math.round((ts - Date.now()) / 60_000)
  if (m <= 0) return 'starting now'
  if (m === 1) return '1 minute'
  if (m < 60) return `${m} minutes`
  const h = Math.floor(m / 60), rem = m % 60
  return rem ? `${h}h ${rem}m` : `${h}h`
}
